import React, { useEffect, useState } from "react";
import {
  Typography,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  Paper,
  TableContainer,
  CircularProgress,
  Box,
  Chip,
} from "@mui/material";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { useAppDispatch } from "../../hooks";
import { showSnackbar } from "../../reducers/snackbarSlice";
import { listPrompts, PromptListItem } from "./PromptsRepository";

const PromptsPage: React.FC = () => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const [prompts, setPrompts] = useState<PromptListItem[]>([]);
  const [loading, setLoading] = useState<boolean>(true);

  useEffect(() => {
    let active = true;
    listPrompts()
      .then((data) => {
        if (active) setPrompts(data);
      })
      .catch((error) => {
        console.error(error);
        dispatch(showSnackbar({ message: t("prompts.load_failed"), severity: "error" }));
      })
      .finally(() => {
        if (active) setLoading(false);
      });
    return () => {
      active = false;
    };
  }, [dispatch, t]);

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" sx={{ mt: 8 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box data-cy="prompts-page">
      <Typography variant="h4" sx={{ mb: 3 }}>
        {t("prompts.title")}
      </Typography>
      <TableContainer component={Paper} elevation={2}>
        <Table data-cy="prompts-table">
          <TableHead>
            <TableRow>
              <TableCell>{t("prompts.name")}</TableCell>
              <TableCell>{t("prompts.current_version")}</TableCell>
              <TableCell align="right">{t("prompts.version_count")}</TableCell>
              <TableCell>{t("prompts.status")}</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {prompts.length === 0 ? (
              <TableRow>
                <TableCell colSpan={4}>{t("prompts.no_prompts")}</TableCell>
              </TableRow>
            ) : (
              prompts.map((prompt) => (
                <TableRow
                  key={prompt.name}
                  hover
                  sx={{ cursor: "pointer" }}
                  onClick={() => navigate(`/prompts/${encodeURIComponent(prompt.name)}`)}
                  data-cy="prompt-row"
                >
                  <TableCell>{prompt.name}</TableCell>
                  <TableCell>{prompt.currentVersion ?? "—"}</TableCell>
                  <TableCell align="right">{prompt.versionCount}</TableCell>
                  <TableCell>
                    {prompt.configured ? (
                      <Chip size="small" color="success" label={t("prompts.configured")} />
                    ) : (
                      <Chip size="small" color="warning" label={t("prompts.not_configured")} />
                    )}
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
};

export default PromptsPage;
